import {
  TaggedError,
  TaggedPublicError,
  ValidationError,
} from "../errors/common";
import { TaggedNetworkError } from "../errors/network";
import { type AxiosError } from "axios";

const GENERIC_SUBSCRIPTION_ERROR_MESSAGE =
  "Ocurrio un error con la suscripcion" as const;

const SubscriptionErrorMessages = {
  BackendDidNotMatchSchema: {
    message: GENERIC_SUBSCRIPTION_ERROR_MESSAGE,
    internal:
      "Backend response did not match the expected schema for subscriptions.",
  },
  FetchingError: {
    message: "Ocurrio un error al obtener tu suscripción",
    internal: "Error fetching subscriptions from the backend.",
  },
  CheckoutSessionError: {
    message: "Ocurrio un error al crear la sesión de pago",
    internal: "Error creating checkout session in the backend.",
  },
  SubscriptionNotFound: {
    message: "La suscripción no fue encontrada",
    internal: "Subscription not found in the backend.",
  },
  SubscriptionAlreadyActive: {
    message: "Ya tienes una suscripción activa",
    internal: "User tried to create a subscription but already has one active.",
  },
  CancelSubscriptionError: {
    message: "Ocurrio un error al cancelar la suscripción, intenta de nuevo más tarde o contacta a soporte",
    internal: "Error cancelling the subscription in the backend.",
  },
  BillingPortalSessionError: {
    message:
      "No pudimos abrir la gestión de facturación. Si tienes suscripción por Stripe, inténtalo de nuevo o contacta a soporte.",
    internal: "Error creating Stripe billing portal session in the backend.",
  },
} as const;

class BackendDidNotMatchSchemaError extends TaggedPublicError<ValidationError> {
  constructor(validationError: ValidationError) {
    super(
      SubscriptionErrorMessages.BackendDidNotMatchSchema.message,
      validationError
    );
  }
}

class FetchingSubscriptionsError extends TaggedPublicError<TaggedNetworkError> {
  constructor(error: TaggedNetworkError) {
    super(SubscriptionErrorMessages.FetchingError.message, error);
  }
}

class CheckoutSessionError extends TaggedPublicError<TaggedNetworkError> {
  constructor(error: TaggedNetworkError, customMessage?: string) {
    super(
      customMessage || SubscriptionErrorMessages.CheckoutSessionError.message,
      error
    );
  }
}

class SubscriptionAlreadyActiveError extends TaggedError(
  "SubscriptionAlreadyActiveError"
) {
  public readonly status: 400;
  constructor() {
    super({
      message: SubscriptionErrorMessages.SubscriptionAlreadyActive.message,
      cause: undefined,
    });
    this.status = 400;
  }
}

class SubscriptionNotFoundError extends TaggedPublicError<TaggedNetworkError> {
  constructor(error: TaggedNetworkError) {
    super(SubscriptionErrorMessages.SubscriptionNotFound.message, error);
  }
}

class CancelSubscriptionError extends TaggedPublicError<TaggedNetworkError> {
  constructor(error: TaggedNetworkError) {
    super(SubscriptionErrorMessages.CancelSubscriptionError.message, error);
  }
}

class BillingPortalSessionError extends TaggedPublicError<TaggedNetworkError> {
  constructor(error: TaggedNetworkError) {
    super(SubscriptionErrorMessages.BillingPortalSessionError.message, error);
  }
}

function repositoryCreateCheckoutSessionHandler(
  error: AxiosError
): SubscriptionAlreadyActiveError | null {
  if (error.response?.status === 400) {
    const data = error.response.data as
      | { error?: string; message?: string }
      | undefined;
    if (String(data?.error).includes("already active")) {
      return new SubscriptionAlreadyActiveError();
    }
  }

  return null;
}

export {
  SubscriptionErrorMessages,
  BackendDidNotMatchSchemaError,
  FetchingSubscriptionsError,
  CheckoutSessionError,
  SubscriptionNotFoundError,
  SubscriptionAlreadyActiveError,
  CancelSubscriptionError,
  BillingPortalSessionError,
  repositoryCreateCheckoutSessionHandler,
};
